// ACTA_AGENT_DETERMINISTIC_ROUTER_V1
// ACTA_AGENT_CONTACT_COMMAND_ROUTER_V1

import {
  availableAgentActions,
} from "@/agent/actionRegistry";

function normalize(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toLocaleLowerCase("fr-FR")
    .replace(
      /[^a-z0-9\s-]/g,
      " "
    )
    .replace(/\s+/g, " ")
    .trim();
}

const NAVIGATION_VERBS = [
  "ouvre",
  "ouvrir",
  "affiche",
  "afficher",
  "montre",
  "montrer",
  "va",
  "aller",
  "accede",
  "acceder",
];

const CONTACT_ACTION_WORDS = [
  "appelle",
  "appeler",
  "appel",
  "contacte",
  "contacter",
  "fiche",
  "ouvre",
  "ouvrir",
];

export function resolveAgentIntent({
  command,
  user,
  hasFeature,
}) {
  const clean = normalize(command);

  const actions =
    availableAgentActions({
      user,
      hasFeature,
    });

  const contactCreate =
    actions.find(
      (action) =>
        action.id ===
        "contact.create"
    );

  const wantsContactCreate =
    /\b(?:cree|creer|ajoute|ajouter|nouveau|nouvelle)\b/u
      .test(clean)
    && /\b(?:contact|prospect|client)\b/u
      .test(clean);

  if (
    contactCreate
    && wantsContactCreate
  ) {
    return contactCreate;
  }

  // ACTA_AGENT_CONTACT_AGENDA_INTENTS_V3
  const meetingRequested =
    /\b(?:rendez vous|rdv|reunion)\b/u
      .test(clean);

  if (meetingRequested) {
    const outcomeAction =
      actions.find(
        (action) =>
          action.id ===
          "meeting.outcome"
      );

    const explicitOutcome =
      /\b(?:resultat|categorise|categoriser|honore|realise|effectue|annule|absence|absent|non honore|a recontacter|a relancer|a ete replanifie|a ete reporte)\b/u
        .test(clean);

    if (
      outcomeAction &&
      explicitOutcome
    ) {
      return outcomeAction;
    }

    const updateAction =
      actions.find(
        (action) =>
          action.id ===
          "meeting.update"
      );

    const wantsUpdate =
      /\b(?:modifie|modifier|decale|decaler|deplace|deplacer|change|changer|replanifie|replanifier|reprogramme|reprogrammer)\b/u
        .test(clean);

    if (
      updateAction &&
      wantsUpdate
    ) {
      return updateAction;
    }

    const createMeeting =
      actions.find(
        (action) =>
          action.id ===
          "meeting.create"
      );

    const wantsCreate =
      /\b(?:cree|creer|ajoute|ajouter|programme|programmer|planifie|planifier|nouveau|nouvelle)\b/u
        .test(clean);

    if (
      createMeeting &&
      wantsCreate
    ) {
      return createMeeting;
    }
  }

  const contactNote =
    actions.find(
      (action) =>
        action.id ===
        "contact.note.add"
    );

  if (
    contactNote &&
    /\b(?:note|commentaire)\b/u
      .test(clean) &&
    /\b(?:ajoute|ajouter|cree|creer|note)\b/u
      .test(clean)
  ) {
    return contactNote;
  }

  const contactFollowup =
    actions.find(
      (action) =>
        action.id ===
        "contact.followup.create"
    );

  if (
    contactFollowup &&
    /\b(?:rappelle|rappeler|relance|relancer|recontacte|recontacter|rappel)\b/u
      .test(clean)
  ) {
    return contactFollowup;
  }

  const explicitContactTarget =
    /\b(?:contact|client|prospect|fiche)\b/u
      .test(clean);

  const contactSearch =
    actions.find(
      (action) =>
        action.id ===
        "contact.search"
    );

  if (
    contactSearch &&
    explicitContactTarget &&
    /\b(?:cherche|chercher|recherche|rechercher|trouve|trouver)\b/u
      .test(clean)
  ) {
    return contactSearch;
  }

  const contactOpen =
    actions.find(
      (action) =>
        action.id ===
        "contact.open"
    );

  if (
    contactOpen &&
    explicitContactTarget &&
    /\b(?:ouvre|ouvrir|affiche|afficher|montre|montrer)\b/u
      .test(clean)
  ) {
    return contactOpen;
  }


  const documentShare =
    actions.find(
      (action) =>
        action.id ===
        "document.share"
    );

  if (
    documentShare
    && /\bpartag(?:e|er|es|ez)\b/u
      .test(clean)
  ) {
    return documentShare;
  }

  const contactWhatsapp =
    actions.find(
      (action) =>
        action.id ===
        "contact.whatsapp"
    );

  const wantsWhatsapp =
    clean.includes(
      "whatsapp"
    )
    || /\bwhats[\s-]*app\b/u
      .test(clean);

  const wantsContactAction =
    CONTACT_ACTION_WORDS.some(
      (word) =>
        clean.includes(word)
    );

  if (
    contactWhatsapp
    && wantsWhatsapp
    && wantsContactAction
  ) {
    return contactWhatsapp;
  }

  const ranked = actions
    .filter(
      (action) =>
        action.kind ===
        "navigation"
    )
    .map((action) => {
      const bestAlias =
        Math.max(
          ...(
            action.aliases || []
          ).map((alias) => {
            const target =
              normalize(alias);

            if (clean === target) {
              return 200;
            }

            if (
              clean.includes(
                target
              )
            ) {
              return (
                100
                + target.length
              );
            }

            return 0;
          }),
          0
        );

      return {
        action,
        score: bestAlias,
      };
    })
    .filter(
      ({ score }) =>
        score > 0
    )
    .sort(
      (left, right) =>
        right.score
        - left.score
    );

  const hasVerb =
    NAVIGATION_VERBS.some(
      (verb) =>
        clean.includes(verb)
    );

  if (
    ranked.length
    && (
      hasVerb
      || ranked[0].score >= 200
    )
  ) {
    return ranked[0].action;
  }

  return null;
}
